/**
 * Canonical field kinds: the language-independent code that decides how a stored form field behaves.
 *
 * `type_name` used to be the discriminator, but it is display text ("Centro de Costos") that changes
 * with the admin's language. Mirrors `purchases/constants/form_fields.py` and
 * `FormFieldCodec.kind` in finnecto-api: same values, same tables, same algorithm. Do not rename.
 */
import type { Field } from '@/types/templates'

export type FieldKind =
  | 'text' | 'paragraph' | 'number' | 'email' | 'phone' | 'link' | 'date' | 'binary_choice'
  | 'amount' | 'currency' | 'single_select' | 'multi_select' | 'file' | 'multi_file'
  | 'file_to_complete' | 'multiline' | 'signature' | 'provider' | 'category' | 'subcategory'
  | 'cost_center' | 'chart_account' | 'product' | 'society' | 'static'

export const FIELD_KINDS: readonly FieldKind[] = [
  'text', 'paragraph', 'number', 'email', 'phone', 'link', 'date', 'binary_choice', 'amount', 'currency',
  'single_select', 'multi_select', 'file', 'multi_file', 'file_to_complete', 'multiline', 'signature',
  'provider', 'category', 'subcategory', 'cost_center', 'chart_account', 'product', 'society', 'static',
]

/** es / en / pt-BR display text each kind has been stored with. */
const LEGACY_TYPE_NAMES: Record<Exclude<FieldKind, 'static'>, readonly [string, string, string]> = {
  text: ['Texto', 'Text', 'Texto'],
  paragraph: ['Párrafo', 'Paragraph', 'Parágrafo'],
  number: ['Número', 'Number', 'Número'],
  binary_choice: ['Elección binaria', 'Binary Choice', 'Escolha Binária'],
  amount: ['Monto', 'Amount', 'Valor'],
  currency: ['Moneda', 'Currency', 'Moeda'],
  email: ['Email', 'Email', 'E-mail'],
  phone: ['Teléfono', 'Phone', 'Telefone'],
  link: ['Link', 'Link', 'Link'],
  date: ['Fecha', 'Date', 'Data'],
  single_select: ['Selección Única', 'Single Selection', 'Seleção Única'],
  multi_select: ['Selección Múltiple', 'Multiple Selection', 'Seleção Múltipla'],
  file: ['Archivo', 'File', 'Arquivo'],
  multi_file: ['Múltiples Archivos', 'Multiple Files', 'Múltiplos Arquivos'],
  file_to_complete: ['Archivo para Completar', 'File to Complete', 'Arquivo para Completar'],
  multiline: ['Multi-línea', 'Multiline', 'Multilinha'],
  signature: ['Firma Digital', 'Digital Signature', 'Assinatura Digital'],
  provider: ['Proveedor', 'Vendor', 'Fornecedor'],
  category: ['Categoría', 'Category', 'Categoria'],
  subcategory: ['Subcategoría', 'Subcategory', 'Subcategoria'],
  cost_center: ['Centro de Costos', 'Cost Center', 'Centro de Custo'],
  chart_account: ['Cuenta Contable', 'Accounting Account', 'Conta Contábil'],
  product: ['Producto', 'Product', 'Produto'],
  society: ['Sociedad', 'Society', 'Sociedade'],
}

/** Same normalization as `normalize_str` in the backend: no accents, ASCII alphanumerics, uppercase. */
export function normalizeToken(value: string): string {
  return value.normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]/g, '').toUpperCase()
}

export const LEGACY_TYPE_NAME_TO_KIND: Record<string, FieldKind> = Object.fromEntries(
  (Object.entries(LEGACY_TYPE_NAMES) as [FieldKind, readonly string[]][]).flatMap(([kind, names]) =>
    names.map((name) => [normalizeToken(name), kind]),
  ),
)

/** The Spanish text still written into `type_name` so readers that have not moved to `field_kind` keep working. */
export const KIND_TO_LEGACY_TYPE_NAME_ES: Record<FieldKind, string> = {
  ...(Object.fromEntries(
    (Object.entries(LEGACY_TYPE_NAMES) as [FieldKind, readonly string[]][]).map(([kind, names]) => [kind, names[0]]),
  ) as Record<Exclude<FieldKind, 'static'>, string>),
  static: 'static',
}

/** `common.field_types.<key>` that renders each kind. */
export const KIND_I18N_KEY: Record<FieldKind, string> = {
  text: 'text',
  paragraph: 'paragraph',
  number: 'number',
  email: 'email',
  phone: 'phone',
  link: 'link',
  date: 'date',
  binary_choice: 'binary_choice',
  amount: 'amount',
  currency: 'currency',
  single_select: 'single_selection',
  multi_select: 'multiple_selection',
  file: 'file',
  multi_file: 'multiple_files',
  file_to_complete: 'file_to_complete',
  multiline: 'multiline',
  signature: 'signature',
  provider: 'provider',
  category: 'category',
  subcategory: 'subcategory',
  cost_center: 'cost_center',
  chart_account: 'accounting_account',
  product: 'product',
  society: 'society',
  static: 'static',
}

const AMOUNT_NAMES = new Set(LEGACY_TYPE_NAMES.amount.map(normalizeToken))
const CURRENCY_NAMES = new Set(LEGACY_TYPE_NAMES.currency.map(normalizeToken))

const STRUCTURAL_KIND_BY_TYPE: Record<string, FieldKind> = {
  toggle: 'binary_choice',
  textarea: 'paragraph',
  phone: 'phone',
  'custom-datepicker': 'date',
  'custom-multi-select': 'multi_select',
  file: 'file',
  multifile: 'multi_file',
  'custom-file-to-complete': 'file_to_complete',
  'custom-multiline': 'multiline',
  'custom-signature': 'signature',
  group: 'amount',
}

const TEXT_KIND_BY_INPUT_TYPE: Record<string, FieldKind> = { number: 'number', email: 'email', url: 'link' }

type KindSource = Pick<Field, 'type'> &
  Partial<Pick<Field, 'type_name' | 'type_icon' | 'inputType' | 'custom_field_id' | 'provider_custom_field_id'>> & {
    field_kind?: FieldKind | string
  }

function isCustomField(field: KindSource): boolean {
  return Boolean(field.custom_field_id || field.provider_custom_field_id)
}

function kindFromTypeName(type: string | undefined, typeName: string | undefined): FieldKind | undefined {
  if (!typeName) return undefined
  const key = normalizeToken(typeName)
  if (AMOUNT_NAMES.has(key)) return type === 'group' ? 'amount' : type === 'text' ? 'number' : undefined
  if (CURRENCY_NAMES.has(key)) return type === 'select' || type === 'custom-select' ? 'currency' : undefined
  return LEGACY_TYPE_NAME_TO_KIND[key]
}

function kindFromStructure(field: KindSource): FieldKind | undefined {
  if (field.type === 'text') return TEXT_KIND_BY_INPUT_TYPE[field.inputType ?? ''] ?? 'text'
  if (field.type === 'custom-select') return field.type_icon || isCustomField(field) ? 'single_select' : undefined
  return STRUCTURAL_KIND_BY_TYPE[field.type]
}

/**
 * Canonical kind of a stored field, or `undefined` when nothing resolves it (reported, never invented).
 * Order: stored `field_kind`; static; legacy `type_name` (ignored for custom fields, where it is the
 * customer's own name); structure.
 */
export function fieldKindOf(field: KindSource | null | undefined): FieldKind | undefined {
  if (!field) return undefined
  if (field.field_kind && (FIELD_KINDS as readonly string[]).includes(field.field_kind)) return field.field_kind as FieldKind
  if (field.type === 'static') return 'static'
  if (!isCustomField(field)) {
    const resolved = kindFromTypeName(field.type, field.type_name)
    if (resolved) return resolved
  }
  return kindFromStructure(field)
}
